import axios from 'axios';

import getAuthSession from './auth';
import { PaginatedResponse } from '../interfaces/companyDetail.interface';
import {
  ActiveFeatureFlag,
  CreateFeatureFlagPayload,
  FeatureFlag,
  FeatureFlagUser,
  UpdateFeatureFlagPayload,
} from '../interfaces/featureFlag.interface';

const BASE_URL = `${process.env.REACT_APP_BASE_API}/feature-flags`;

async function authHeaders(): Promise<{ Authorization: string }> {
  const authSession = await getAuthSession();
  return { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` };
}

/**
 * Active, in-window flags (codename + public + type targeting) for the current
 * user to evaluate client-side against their own user.type. Any authenticated
 * user may call this.
 */
export async function getActiveFeatureFlags(): Promise<ActiveFeatureFlag[]> {
  const response = await axios.get<ActiveFeatureFlag[]>(
    `${BASE_URL}/active/`,
    { headers: await authHeaders() },
  );
  return response.data;
}

export async function getFeatureFlags(
  params: { limit?: number; offset?: number } = {},
): Promise<PaginatedResponse<FeatureFlag>> {
  const response = await axios.get<PaginatedResponse<FeatureFlag>>(
    `${BASE_URL}/`,
    { headers: await authHeaders(), params },
  );
  return response.data;
}

export async function createFeatureFlag(payload: CreateFeatureFlagPayload): Promise<FeatureFlag> {
  const response = await axios.post<FeatureFlag>(
    `${BASE_URL}/`,
    payload,
    { headers: await authHeaders() },
  );
  return response.data;
}

export async function patchFeatureFlag(id: number, payload: UpdateFeatureFlagPayload): Promise<FeatureFlag> {
  const response = await axios.patch<FeatureFlag>(
    `${BASE_URL}/${id}/`,
    payload,
    { headers: await authHeaders() },
  );
  return response.data;
}

export async function deleteFeatureFlag(id: number): Promise<void> {
  await axios.delete(`${BASE_URL}/${id}/`, { headers: await authHeaders() });
}

export async function getFeatureFlagUsers(
  id: number,
  params: { limit?: number; offset?: number } = {},
): Promise<PaginatedResponse<FeatureFlagUser>> {
  const response = await axios.get<PaginatedResponse<FeatureFlagUser>>(
    `${BASE_URL}/${id}/users/`,
    { headers: await authHeaders(), params },
  );
  return response.data;
}

/** Users matching an email typeahead, for individual flag assignment. */
export async function searchUsersByEmail(email: string): Promise<FeatureFlagUser[]> {
  if (email.length < 2) return [];
  const response = await axios.get<PaginatedResponse<FeatureFlagUser>>(
    `${process.env.REACT_APP_BASE_API}/users/`,
    { headers: await authHeaders(), params: { email } },
  );
  return response.data.results.map((user) => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
  }));
}

export async function assignFeatureFlagUsers(id: number, userIds: number[]): Promise<FeatureFlag> {
  const response = await axios.post<FeatureFlag>(
    `${BASE_URL}/${id}/assign-users/`,
    { userIds },
    { headers: await authHeaders() },
  );
  return response.data;
}

export async function removeFeatureFlagUsers(id: number, userIds: number[]): Promise<FeatureFlag> {
  const response = await axios.post<FeatureFlag>(
    `${BASE_URL}/${id}/remove-users/`,
    { userIds },
    { headers: await authHeaders() },
  );
  return response.data;
}
